"use client";
import { byPrefixAndName } from "@awesome.me/kit-12cf79935c/icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { format, useMask } from "@react-input/mask";
import clsx from "clsx";
import { useLocale } from "next-intl";
import Image from "next/image";
import {
  forwardRef,
  memo,
  useCallback,
  useEffect,
  useId,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from "react";
import { Modal } from "../Modal";
import Spinner from "../Spinner";
import { commonService } from "@/services/commonService";
import { useTranslationDetector } from "@/hooks/useTranslationDetector";
import { useIsMobile } from "@/hooks/useIsMobile";
import { useModal } from "@/contexts/ModalContext";
import { cdnFlagsPath } from "@/constants/cdnUrls";

// ============================================================================
// INPUT COMPONENT
// ============================================================================
const Input = memo(
  forwardRef(
    (
      { label, error, className = "", id, name, disabled = false, ...props },
      ref,
    ) => {
      const generatedInputId = useId();
      const inputId =
        id || name || `input-${generatedInputId.replace(/:/g, "")}`;

      return (
        <div className={clsx("flex h-full flex-col", className)}>
          <input
            ref={ref}
            id={inputId}
            name={name}
            disabled={disabled}
            className={clsx(
              "h-full w-full rounded-none border-0 px-3.5 py-[14px] font-medium transition-all duration-200 outline-none focus:ring-0 ltr:rounded-r-md rtl:rounded-l-md rtl:text-right",
              "disabled:bg-background-global disabled:cursor-not-allowed",
            )}
            {...props}
          />
        </div>
      );
    },
  ),
);

Input.displayName = "Input";

// ============================================================================
// SELECT OPTION COMPONENT (Memoized for performance)
// ============================================================================
const SelectOption = memo(
  ({ option, isSelected, onSelect, flagCdnBasePath }) => (
    <button
      type="button"
      onClick={() => onSelect(option.value)}
      className={clsx(
        "hover:bg-background-global flex w-full cursor-pointer items-center justify-between gap-2 rounded-lg border-gray-200 p-4 text-left transition-colors duration-150 md:mb-0 md:rounded-none md:border-0 md:border-b-0 md:px-3.5 md:py-2.5",
        isSelected && "bg-background-global font-medium",
      )}
    >
      <div className="flex items-center gap-2">
        <Image
          src={`${flagCdnBasePath}${option.value.toLowerCase()}.svg`}
          alt={option.label}
          width={24}
          height={24}
          className="h-6 w-6 overflow-hidden rounded-full object-cover"
          quality={100}
          loading="lazy"
        />
        <span>{option.phonePrefix}</span>
        <span>{option.countryName}</span>
      </div>
      {isSelected && (
        <FontAwesomeIcon
          icon={byPrefixAndName.far["check"]}
          className="text-teal text-sm"
        />
      )}
    </button>
  ),
);

SelectOption.displayName = "SelectOption";

// ============================================================================
// SELECT COMPONENT
// ============================================================================
const Select = memo(
  forwardRef(
    (
      {
        label,
        error,
        options = [],
        value,
        onChange,
        placeholder = "Seçiniz",
        disabled = false,
        className = "",
        id,
        name,
        showPlaceholder = true,
        searchable = true,
        ...props
      },
      ref,
    ) => {
      const t = useTranslationDetector();
      const isMobile = useIsMobile();
      const [isOpen, setIsOpen] = useState(false);
      const [searchTerm, setSearchTerm] = useState("");
      const generatedSelectId = useId();

      const selectId =
        id || name || `select-${generatedSelectId.replace(/:/g, "")}`;
      const modalId = `select-modal-${selectId}`;

      const modal = useModal(modalId);

      const dropdownRef = useRef(null);
      const internalRef = useRef(null);
      const searchInputRef = useRef(null);

      useImperativeHandle(ref, () => internalRef.current);

      const selectedOption = useMemo(
        () => options.find((opt) => opt.value === value),
        [options, value],
      );

      const filteredOptions = useMemo(() => {
        if (!searchTerm) return options;
        const lowerSearchTerm = searchTerm.toLowerCase();
        return options.filter(
          (opt) =>
            opt.label.toLowerCase().includes(lowerSearchTerm) ||
            opt.value.toLowerCase().includes(lowerSearchTerm),
        );
      }, [options, searchTerm]);

      useEffect(() => {
        if (!isMobile) {
          const handleClickOutside = (event) => {
            if (
              dropdownRef.current &&
              !dropdownRef.current.contains(event.target)
            ) {
              setIsOpen(false);
              setSearchTerm("");
            }
          };

          if (isOpen) {
            document.addEventListener("mousedown", handleClickOutside);
            setTimeout(() => searchInputRef.current?.focus(), 50);
          }

          return () =>
            document.removeEventListener("mousedown", handleClickOutside);
        }
      }, [isOpen, isMobile]);

      const handleSelect = useCallback(
        (optionValue) => {
          const syntheticEvent = {
            target: { value: optionValue, name },
            currentTarget: { value: optionValue, name },
          };
          onChange?.(syntheticEvent);
          setIsOpen(false);
          setSearchTerm("");
          if (isMobile) {
            modal.close();
          }
        },
        [onChange, name, isMobile, modal],
      );

      const handleToggle = useCallback(() => {
        if (!disabled) {
          if (isMobile) {
            modal.open({
              isFullScreen: false,
              isBottomSheet: true,
              headerClassName: "!border-0",
              contentClassName:
                "!px-4 !pb-4 !pt-0 flex-1 overflow-y-auto p-6 md:px-6 md:pt-4 md:pb-5",

              title: label || t("common.select"),
            });
          } else {
            setIsOpen((prev) => !prev);
          }
        }
      }, [disabled, isMobile, modal, label, t]);

      const handleSearchChange = useCallback((e) => {
        setSearchTerm(e.target.value);
      }, []);

      const renderDropdownContent = useMemo(
        () => (
          <>
            {searchable && (
              <div className="sticky top-0 bg-white p-0 md:p-2">
                <div className="relative">
                  <span className="absolute top-1/2 left-4 -translate-y-1/2 text-gray-500 md:hidden">
                    <FontAwesomeIcon
                      icon={byPrefixAndName.fal["magnifying-glass"]}
                    />
                  </span>
                  <input
                    ref={searchInputRef}
                    type="text"
                    placeholder={t("common.search")}
                    value={searchTerm}
                    onChange={handleSearchChange}
                    className="border-primary-weak focus:border-primary w-full rounded-md border-2 p-4 pl-11 outline-none focus:ring-0 md:px-3 md:py-2 md:pl-4"
                    onClick={(e) => e.stopPropagation()}
                  />
                  {searchTerm && (
                    <button
                      onClick={() => {
                        setSearchTerm("");
                        searchInputRef.current?.focus();
                      }}
                      className="bg-background-400 text-foreground-muted absolute top-1/2 right-4 flex h-6 w-6 -translate-y-1/2 items-center justify-center overflow-hidden rounded-full md:hidden"
                    >
                      <FontAwesomeIcon
                        icon={byPrefixAndName.far["xmark"]}
                        className="text-sm"
                      />
                    </button>
                  )}
                </div>
              </div>
            )}

            <div className="scrollbar-default mt-4 h-[calc(100%-76px)] min-h-[500px] overflow-y-auto pr-0.5 md:m-0 md:max-h-64 md:pr-0">
              {filteredOptions.length > 0 ? (
                filteredOptions.map((option) => (
                  <SelectOption
                    key={option.value}
                    option={option}
                    isSelected={option.value === value}
                    onSelect={handleSelect}
                    flagCdnBasePath={cdnFlagsPath}
                  />
                ))
              ) : (
                <div className="px-3.5 py-6 text-center text-gray-500">
                  {t("common.noResults")}
                </div>
              )}
            </div>
          </>
        ),
        [
          searchable,
          searchTerm,
          handleSearchChange,
          filteredOptions,
          value,
          handleSelect,
          t,
        ],
      );

      return (
        <div className={clsx("relative flex h-full flex-col", className)}>
          <div ref={dropdownRef} className="relative h-full">
            <button
              ref={internalRef}
              type="button"
              id={selectId}
              onClick={handleToggle}
              disabled={disabled}
              onFocus={props.onFocus}
              onBlur={props.onBlur}
              className={clsx(
                "bg-background-global flex h-full w-full cursor-pointer items-center justify-between gap-2 px-3.5 py-2.5 text-left text-base transition-all duration-200 outline-none focus:ring-0 ltr:rounded-l-md rtl:rounded-r-md",
                "disabled:bg-background-global h-[53px] disabled:cursor-not-allowed disabled:!pr-0 disabled:text-gray-500",
              )}
            >
              <span
                className={clsx(
                  selectedOption ? "text-gray-900" : "text-gray-500",
                )}
              >
                {selectedOption ? (
                  <div className="flex w-full items-center justify-start gap-2">
                    <Image
                      src={`${cdnFlagsPath}${selectedOption.value.toLowerCase()}.svg`}
                      alt={selectedOption.label}
                      width={24}
                      height={24}
                      className="h-6 w-6 overflow-hidden rounded-full object-cover"
                      quality={100}
                    />
                    <span>{selectedOption.phonePrefix}</span>
                  </div>
                ) : showPlaceholder ? (
                  placeholder
                ) : (
                  <div className="flex items-center gap-2">
                    <Spinner className="h-4 w-4 animate-spin" />
                  </div>
                )}
              </span>
              {!disabled && (
                <FontAwesomeIcon
                  icon={byPrefixAndName.fal["angle-down"]}
                  className={clsx(
                    "text-foreground transition-transform duration-200",
                    isOpen && "rotate-180",
                  )}
                />
              )}
            </button>

            <input type="hidden" name={name} value={value || ""} readOnly />

            {/* Desktop: Dropdown */}
            {!isMobile && isOpen && (
              <div className="shadow-xxl absolute top-full right-0 left-0 z-50 mt-2 max-h-80 overflow-hidden rounded-md bg-white md:w-75">
                {renderDropdownContent}
              </div>
            )}

            {/* Mobile: Modal */}
            {isMobile && <Modal id={modalId}>{renderDropdownContent}</Modal>}
          </div>
        </div>
      );
    },
  ),
);

Select.displayName = "Select";

// ============================================================================
// PHONE NUMBER COMPONENT
// ============================================================================
const PhoneNumber = forwardRef(
  (
    {
      value,
      defaultCountry = "TR",
      name = "phone",
      countryName = "countryCode",
      localNumberName = "localNumber",
      onChange,
      onPartsChange,
      disabled = false,
      required = false,
      className = "",
      allowedCountries,
      initialData,
      labels = {},
      placeholders = {},
      withFlag = false,
      onValidationChange,
      error: externalError,
      clearOnCountryChange = false,
      onFocus: externalOnFocus, // 🆕 onFocus'u ayır
      ...props
    },
    ref,
  ) => {
    const t = useTranslationDetector();
    const isMobile = useIsMobile();

    const [countryCodes, setCountryCodes] = useState(initialData || []);
    const [localizedCountryCodes, setLocalizedCountryCodes] = useState([]);
    const [isLoading, setIsLoading] = useState(!initialData);
    const [selectedCountryCode, setSelectedCountryCode] =
      useState(defaultCountry);
    const [localNumber, setLocalNumber] = useState("");
    const [combinedValue, setCombinedValue] = useState("");
    const [internalError, setInternalError] = useState(null);
    const locale = useLocale();

    const error = externalError !== undefined ? externalError : internalError;

    const countrySelectRef = useRef(null);
    const phoneInputRef = useRef(null);
    const hiddenInputRef = useRef(null);
    const lastParsedValueRef = useRef(null);

    // 🆕 TR için mask options
    const maskOptions = useMemo(() => {
      return {
        mask: "5__ ___ ____",
        replacement: { _: /\d/ },
        showMask: true,
        track: ({ data }) => {
          if (!data) return data;

          const digits = data.replace(/\D/g, "");

          // Kullanıcı tek tek yazıyorsa
          if (digits.length === 1) {
            return digits;
          }

          // Paste / autofill geldiyse
          if (digits.startsWith("5")) {
            return digits.slice(1);
          }

          return digits;
        },
      };
    }, []);

    // 🆕 TR için mask ref
    const maskRef = useMask(maskOptions);

    function sortCountriesByPriority(countries) {
      const priorityOrder = ["TR", "GB", "DE", "RU", "AZ", "SA"];

      return countries.sort((a, b) => {
        const indexA = priorityOrder.indexOf(a.CountryCode);
        const indexB = priorityOrder.indexOf(b.CountryCode);

        if (indexA !== -1 && indexB !== -1) {
          return indexA - indexB;
        }

        if (indexA !== -1) {
          return -1;
        }

        if (indexB !== -1) {
          return 1;
        }

        return a.CountryCode.localeCompare(b.CountryCode);
      });
    }

    function getCountryName(languageCode, countryCode, data) {
      const country = data.find(
        (item) =>
          item.LanguageCode === languageCode &&
          item.CountryCode === countryCode,
      );

      return country ? country.CountryName : null;
    }

    // Fetch country codes
    useEffect(() => {
      if (initialData) {
        setCountryCodes(initialData);
        setIsLoading(false);
        return;
      }

      const fetchCountryCodes = async () => {
        try {
          setIsLoading(true);

          const countries = await commonService.getLocalizedCountries();

          if (countries?.LocalizedCountries.length > 0) {
            setLocalizedCountryCodes(countries.LocalizedCountries);
          }

          const response = await commonService.getCountryCodes();

          if (response?.Countries) {
            const sortedCountries = sortCountriesByPriority(response.Countries);
            setCountryCodes(sortedCountries);
          }
        } catch {
          // API hata durumunda dropdown bos kalsin; UI loading kapatilir.
        } finally {
          setIsLoading(false);
        }
      };

      fetchCountryCodes();
    }, [initialData]);

    // 🚀 PERFORMANCE: Memoized country code lookup map
    const countryCodesMap = useMemo(() => {
      const map = new Map();
      countryCodes.forEach((country) => {
        if (country?.CountryCode && country?.PhonePrefix) {
          map.set(country.CountryCode, country);
          map.set(country.PhonePrefix, country);
        }
      });
      return map;
    }, [countryCodes]);

    // Parse value prop
    const parseValue = useCallback(
      (inputValue) => {
        if (!inputValue || typeof inputValue !== "string") {
          return { countryCode: defaultCountry, localNumber: "" };
        }

        let normalized = inputValue.trim();
        if (normalized.startsWith("00")) {
          normalized = `+${normalized.substring(2)}`;
        }

        const parts = normalized.split("-");
        if (parts.length >= 2) {
          const prefix = parts[0] || "";
          const local = parts.slice(1).join("-") || "";

          const country =
            countryCodesMap.get(prefix) ||
            countryCodes.find(
              (c) =>
                c?.PhonePrefix &&
                prefix &&
                c.PhonePrefix.replace("+", "") === prefix.replace("+", ""),
            );

          if (country && country.PhonePrefix) {
            if (
              allowedCountries &&
              !allowedCountries.includes(country.CountryCode)
            ) {
              const firstAllowed = countryCodes.find(
                (c) =>
                  c?.CountryCode && allowedCountries.includes(c.CountryCode),
              );
              if (firstAllowed) {
                return {
                  countryCode: firstAllowed.CountryCode || defaultCountry,
                  localNumber:
                    typeof local === "string" ? local.replace(/\D/g, "") : "",
                };
              }
            }
            return {
              countryCode: country.CountryCode || defaultCountry,
              localNumber:
                typeof local === "string" ? local.replace(/\D/g, "") : "",
            };
          }
        }

        if (normalized.startsWith("+")) {
          for (const country of countryCodes) {
            if (
              !country?.PhonePrefix ||
              typeof country.PhonePrefix !== "string"
            ) {
              continue;
            }
            if (normalized.startsWith(country.PhonePrefix)) {
              const local =
                normalized.substring(country.PhonePrefix.length) || "";
              if (
                allowedCountries &&
                !allowedCountries.includes(country.CountryCode)
              ) {
                continue;
              }
              return {
                countryCode: country.CountryCode || defaultCountry,
                localNumber:
                  typeof local === "string" ? local.replace(/\D/g, "") : "",
              };
            }
          }
        }

        const digitsOnly =
          typeof normalized === "string" ? normalized.replace(/\D/g, "") : "";
        return {
          countryCode: defaultCountry,
          localNumber: digitsOnly,
        };
      },
      [countryCodesMap, countryCodes, defaultCountry, allowedCountries],
    );

    // Build combined value
    const buildCombinedValue = useCallback(
      (countryCode, local) => {
        if (!countryCode || !local) {
          return "";
        }

        const country = countryCodesMap.get(countryCode);
        if (!country || !country.PhonePrefix) {
          return "";
        }

        const prefix = country.PhonePrefix;
        const digitsOnly =
          typeof local === "string" ? local.replace(/\D/g, "") : "";

        if (!digitsOnly) {
          return "";
        }

        return `${prefix}-${digitsOnly}`;
      },
      [countryCodesMap],
    );

    // Validate phone number
    const validatePhoneNumber = useCallback(
      (local) => {
        if (!local || typeof local !== "string") {
          if (required) {
            return t("common.required");
          }
          return null;
        }

        const digitsOnly = local.replace(/\D/g, "");

        if (!digitsOnly) {
          if (required) {
            return t("common.required");
          }
          return null;
        }

        if (digitsOnly.length < 6 || digitsOnly.length > 15) {
          return t("common.invalidPhone");
        }

        return null;
      },
      [required, t],
    );

    // Update combined value and trigger callbacks
    const updateCombinedValue = useCallback(
      (countryCode, local) => {
        const combined = buildCombinedValue(countryCode, local);
        setCombinedValue(combined);

        if (hiddenInputRef.current) {
          hiddenInputRef.current.value = combined;
        }

        onChange?.(combined);

        const country = countryCodesMap.get(countryCode);
        onPartsChange?.({
          countryCode: countryCode || "",
          prefix: country?.PhonePrefix || "",
          local: typeof local === "string" ? local.replace(/\D/g, "") : "",
        });

        const validationError = validatePhoneNumber(local);
        setInternalError(validationError);
        onValidationChange?.(!validationError);
      },
      [
        buildCombinedValue,
        onChange,
        onPartsChange,
        countryCodesMap,
        validatePhoneNumber,
        onValidationChange,
      ],
    );

    // 🔧 Handle country change
    const handleCountryChange = useCallback(
      (e) => {
        const newCountryCode = e.target.value;
        const prevCountryCode = selectedCountryCode;
        setSelectedCountryCode(newCountryCode);

        // 🆕 clearOnCountryChange true ise veya numara yoksa temizle
        if (clearOnCountryChange || !localNumber || !localNumber.trim()) {
          setLocalNumber("");
          setCombinedValue("");
          if (hiddenInputRef.current) {
            hiddenInputRef.current.value = "";
          }
          onChange?.("");

          const country = countryCodesMap.get(newCountryCode);
          onPartsChange?.({
            countryCode: newCountryCode || "",
            prefix: country?.PhonePrefix || "",
            local: "",
          });

          setInternalError(null);

          // 🆕 TR için mask input'u temizle
          if (newCountryCode === "TR" && maskRef.current) {
            maskRef.current.value = "";
          }
        } else {
          // 🆕 Numara varsa koru, sadece prefix değiştir
          // TR'ye geçerken mask formatına çevir
          if (newCountryCode === "TR" && maskRef.current && maskOptions) {
            const numberWithout5 = localNumber.startsWith("5")
              ? localNumber.slice(1)
              : localNumber;
            const formatted = format(numberWithout5, maskOptions);
            maskRef.current.value = formatted;
          }
          // TR'den başka ülkeye geçerken normal input'a değeri set et
          else if (prevCountryCode === "TR" && phoneInputRef.current) {
            phoneInputRef.current.value = localNumber;
          }

          updateCombinedValue(newCountryCode, localNumber);
        }

        // 🆕 Focus yönetimi
        if (newCountryCode === "TR" && prevCountryCode !== "TR" && !isMobile) {
          setTimeout(() => {
            if (maskRef.current) {
              maskRef.current.focus();
              maskRef.current.setSelectionRange(1, 1);
            }
          }, 100);
        } else if (newCountryCode !== "TR" && !isMobile) {
          setTimeout(() => {
            phoneInputRef.current?.focus();
          }, 100);
        }
      },
      [
        selectedCountryCode,
        localNumber,
        clearOnCountryChange,
        updateCombinedValue,
        countryCodesMap,
        onChange,
        onPartsChange,
        isMobile,
        maskRef,
        maskOptions,
      ],
    );

    // 🔧 Handle phone number change
    const handlePhoneChange = useCallback(
      (e) => {
        const inputValue = e.target.value;
        const digitsOnly = inputValue.replace(/\D/g, "");
        setLocalNumber(digitsOnly);
        updateCombinedValue(selectedCountryCode, digitsOnly);
      },
      [selectedCountryCode, updateCombinedValue],
    );

    // TR icin input scaffold'u (5__ ___ ____) her zaman gorunur olsun
    // Aksi halde useMask scaffold'u focus'a kadar yazmadiginda placeholder gri gozukur
    useEffect(() => {
      if (selectedCountryCode !== "TR") return;
      if (!maskRef.current || !maskOptions) return;
      const digitsOnly =
        typeof localNumber === "string" ? localNumber.replace(/\D/g, "") : "";
      if (digitsOnly) return;
      if (maskRef.current.value) return;
      const scaffold = format("", maskOptions) || "5__ ___ ____";
      maskRef.current.value = scaffold;
    }, [
      selectedCountryCode,
      localNumber,
      maskOptions,
      countryCodes.length,
      maskRef,
    ]);

    // Initialize from value prop
    useEffect(() => {
      if (countryCodes.length === 0) return;

      if (value !== undefined && value !== null && value !== "") {
        if (lastParsedValueRef.current === value) {
          return;
        }

        const parsed = parseValue(value);

        if (
          parsed.countryCode !== selectedCountryCode ||
          parsed.localNumber !== localNumber.replace(/\D/g, "")
        ) {
          setSelectedCountryCode(parsed.countryCode);
          setLocalNumber(parsed.localNumber);

          // 🆕 TR için mask input'una initial değeri set et
          if (parsed.countryCode === "TR" && maskRef.current && maskOptions) {
            const numberWithout5 = parsed.localNumber.startsWith("5")
              ? parsed.localNumber.slice(1)
              : parsed.localNumber;
            const formatted = format(numberWithout5, maskOptions);
            maskRef.current.value = formatted;
          }

          updateCombinedValue(parsed.countryCode, parsed.localNumber);
        }
        lastParsedValueRef.current = value;
      } else if (
        (value === "" || value === null) &&
        lastParsedValueRef.current !== value &&
        lastParsedValueRef.current !== null
      ) {
        // Sadece daha once parse edilmis bir deger varken bosa dusuyorsa temizle.
        // Ilk mount + bos value durumunda useMask scaffold'unu bozmayalim.
        setLocalNumber("");
        if (selectedCountryCode === "TR" && maskRef.current) {
          maskRef.current.value = "";
        }
        lastParsedValueRef.current = value;
      }
    }, [
      value,
      countryCodes,
      parseValue,
      updateCombinedValue,
      selectedCountryCode,
      localNumber,
      maskOptions,
      maskRef,
    ]);

    // Expose methods via ref
    useImperativeHandle(
      ref,
      () => ({
        getValue: () => combinedValue,
        setValue: (newValue) => {
          const parsed = parseValue(newValue);
          setSelectedCountryCode(parsed.countryCode);
          setLocalNumber(parsed.localNumber);
          updateCombinedValue(parsed.countryCode, parsed.localNumber);
        },
        getCountryCode: () => selectedCountryCode,
        getLocalNumber: () =>
          typeof localNumber === "string" ? localNumber.replace(/\D/g, "") : "",
        focus: () => {
          if (selectedCountryCode === "TR" && maskRef.current) {
            maskRef.current.focus();
            // 🆕 Cursor'u 5'in hemen yanına (index 1) taşı
            setTimeout(() => {
              if (maskRef.current) {
                maskRef.current.setSelectionRange(1, 1);
              }
            }, 0);
          } else {
            phoneInputRef.current?.focus();
          }
        },
        blur: () => {
          if (selectedCountryCode === "TR" && maskRef.current) {
            maskRef.current.blur();
          } else {
            phoneInputRef.current?.blur();
          }
        },
      }),
      [
        combinedValue,
        selectedCountryCode,
        localNumber,
        parseValue,
        updateCombinedValue,
        maskRef,
      ],
    );

    // 🚀 PERFORMANCE: Memoized available countries
    const availableCountries = useMemo(() => {
      return allowedCountries
        ? countryCodes.filter((c) => allowedCountries.includes(c.CountryCode))
        : countryCodes;
    }, [allowedCountries, countryCodes]);

    // 🚀 PERFORMANCE: Memoized country options
    const countryOptions = useMemo(() => {
      return availableCountries
        .filter((country) => country?.CountryCode && country?.PhonePrefix)
        .map((country) => ({
          phonePrefix: country.PhonePrefix,
          countryName:
            getCountryName(
              locale.toUpperCase(),
              country.CountryCode,
              localizedCountryCodes,
            ) || country.CountryCode,
          value: country.CountryCode,
          label: withFlag
            ? `${country.CountryCode} ${country.PhonePrefix} ${getCountryName(locale.toUpperCase(), country.CountryCode, localizedCountryCodes) || ""}`
            : `${country.CountryCode} ${country.PhonePrefix} ${getCountryName(locale.toUpperCase(), country.CountryCode, localizedCountryCodes) || ""}`,
        }));
    }, [availableCountries, withFlag, locale, localizedCountryCodes]);

    return (
      <div className={clsx("flex w-full flex-col gap-2", className)}>
        <div
          className={clsx(
            "flex h-[56px] rounded-md border",
            error ? "border-danger" : "border-gray-300",
          )}
        >
          {/* Country Code Select */}
          <div className="flex-shrink-0">
            <Select
              ref={countrySelectRef}
              label={labels.country || t("common.country")}
              error={null}
              required={required}
              value={selectedCountryCode}
              options={isLoading ? [] : countryOptions}
              placeholder={isLoading ? t("common.loading") : t("common.select")}
              disabled={disabled || isLoading}
              name={countryName}
              id={`${props.id || name}-country`}
              onChange={handleCountryChange}
              showPlaceholder={!isLoading}
            />
          </div>

          {/* Phone Number Input */}
          <div className="flex-1">
            <Input
              ref={selectedCountryCode === "TR" ? maskRef : phoneInputRef}
              type="tel"
              label={labels.phone || t("common.phone")}
              error={null}
              required={required}
              key={selectedCountryCode}
              value={selectedCountryCode === "TR" ? undefined : localNumber}
              disabled={disabled}
              placeholder={
                selectedCountryCode === "TR"
                  ? "5__ ___ ____"
                  : placeholders.phone || t("common.phonePlaceholder")
              }
              name={localNumberName}
              id={`${props.id || name}-phone`}
              onChange={handlePhoneChange}
              onFocus={(e) => {
                // 🆕 TR için manuel focus'ta da cursor'u 5'in yanına getir
                if (selectedCountryCode === "TR") {
                  // Input bos ise useMask otomatik scaffold yazmayabilir; biz yazalim
                  if (!e.target.value && maskOptions) {
                    const scaffold = format("", maskOptions) || "5__ ___ ____";
                    e.target.value = scaffold;
                  }
                  setTimeout(() => {
                    const currentValue = e.target.value || "";
                    const lastDigitIndex = currentValue.search(/\d(?=[^\d]*$)/);
                    const position =
                      lastDigitIndex !== -1 ? lastDigitIndex + 1 : 1;
                    e.target.setSelectionRange(position, position);
                  }, 0);
                }
                // Eğer dışarıdan onFocus prop'u geliyorsa onu da çağır
                externalOnFocus?.(e);
              }}
              aria-describedby={
                error ? `${props.id || name}-phone-error` : undefined
              }
              aria-invalid={error ? "true" : "false"}
              {...props}
            />
          </div>
        </div>

        {/* Error message */}
        {error && (
          <p
            className="text-danger flex items-center gap-1 pl-4 text-xs"
            id={`${props.id || name}-phone-error`}
          >
            <FontAwesomeIcon icon={byPrefixAndName.far["circle-exclamation"]} />
            <span>
              {typeof error === "string" ? error : t("common.required")}
            </span>
          </p>
        )}

        {/* Hidden input for form submission */}
        <input
          ref={hiddenInputRef}
          type="hidden"
          name={name}
          value={combinedValue}
          readOnly
        />
      </div>
    );
  },
);

PhoneNumber.displayName = "PhoneNumber";

const MemoizedPhoneNumber = memo(PhoneNumber);
MemoizedPhoneNumber.displayName = "PhoneNumber";

export default MemoizedPhoneNumber;
